/**
 * Pure helpers to build media URLs from publisher experience JSON (no ExperienceData).
 */
import type { ExperiencePayload } from "@/types/experience";
import type { HostPixmoto } from "@/types/globals";

type Body = ExperiencePayload | null | undefined;

const VIDEO_TYPE = "mp4";

/**
 * Default rendition for mobile when the experience has no explicit per-device
 * default (video.defaults[device]). A phone renders the player at ~400px, so a
 * higher rendition only wastes bandwidth. 360p exists for effectively all
 * videos; if it is missing we fall back to a low tier.
 */
export const MOBILE_DEFAULT_MODE = "360p";

function pickMobileMode(modes: string[] | null): string | null {
  if (!modes || !modes.length) {
    return null;
  }
  for (let i = 0; i < modes.length; i++) {
    if (modes[i] === MOBILE_DEFAULT_MODE) {
      return modes[i];
    }
  }
  // No 360p rendition — take a low tier of the ascending ladder.
  return modes.length > 2 ? modes[1] : modes[0];
}

function formatTemplate(template: string, mode: string | null): string {
  if (!template) {
    return "";
  }
  return String(template).replace(/{(\d+)}/g, function (_match, number) {
    return mode != null ? String(mode) : "";
  });
}

function getVideoModes(body: Body): string[] | null {
  if (!body || !body.video || !body.video.modes) {
    return null;
  }
  const modes = body.video.modes[VIDEO_TYPE];
  return modes && modes.length ? modes : null;
}

function getHighDefVideoMode(body: Body, orientation: string): string | null {
  const modes = getVideoModes(body);
  if (!modes) {
    return null;
  }
  if (orientation === "portrait") {
    if (modes.length > 2) {
      return modes[1];
    }
    return modes[0];
  }
  return modes[modes.length - 1];
}

function getDefaultVideoMode(
  body: Body,
  device: string | null | undefined,
): string | null {
  const defaults = body && body.video && body.video.defaults;
  if (defaults && device && defaults[device] && defaults[device].mode != null) {
    return defaults[device].mode;
  }
  if (device === "mobile") {
    return pickMobileMode(getVideoModes(body));
  }
  return getHighDefVideoMode(body, "landscape");
}

function normalizeHostname(host: string | null | undefined): string {
  if (!host) {
    return "";
  }
  let h = String(host).toLowerCase();
  const colon = h.indexOf(":");
  if (colon >= 0 && h.indexOf("[") < 0) {
    h = h.slice(0, colon);
  }
  return h;
}

/**
 * Hosts where preview-server (or equivalent) serves /proxy/media.pixmoto.com/.
 * Real publisher sites use media.pixmoto.com directly for warm + playback.
 */
export function hostUsesMediaProxy(
  hostname: string | null | undefined,
): boolean {
  const h = normalizeHostname(hostname);
  if (!h) {
    return false;
  }
  if (h === "localhost" || h === "127.0.0.1") {
    return true;
  }
  if (h === "automaze.dev" || h.endsWith(".automaze.dev")) {
    return true;
  }
  if (h === "play.pixmoto.loc" || h.endsWith(".play.pixmoto.loc")) {
    return true;
  }
  return false;
}

function normalizeMediaHost(mediaServer: string | null | undefined): string {
  if (!mediaServer) {
    return resolveMediaServerHost();
  }
  return String(mediaServer).replace(/^\/+/, "");
}

/**
 * Media host/path for video URLs — proxy only on dev/preview hosts (see hostUsesMediaProxy).
 */
export function resolveMediaServerHost(pm?: HostPixmoto | null): string {
  const bucket =
    pm || (typeof window !== "undefined" ? window.__pixmoto : null);
  if (bucket && bucket.mediaServer) {
    return String(bucket.mediaServer).replace(/^\/+/, "");
  }
  if (typeof window !== "undefined" && window.ENVIRONMENT === "production") {
    return "media.pixmoto.com";
  }
  if (typeof location !== "undefined" && location.host) {
    const hostname = location.hostname || normalizeHostname(location.host);
    if (hostUsesMediaProxy(hostname)) {
      return `${location.host}/proxy/media.pixmoto.com`;
    }
  }
  return "media.pixmoto.com";
}

/**
 * @param {object} body Normalized experience body
 * @param {{ protocol?: string, mediaServer?: string, device?: string }} cfg
 * @returns {string|null}
 */
export function buildDefaultVideoUrl(
  body: Body,
  cfg?: {
    protocol?: string;
    mediaServer?: string | null;
    device?: string;
  } | null,
): string | null {
  if (!body || !body.video || !body.video.template) {
    return null;
  }
  const mode = getDefaultVideoMode(body, cfg && cfg.device);
  if (mode == null) {
    return null;
  }
  const filename = `${formatTemplate(body.video.template, mode)}.${VIDEO_TYPE}`;
  const protocol =
    (cfg && cfg.protocol) ||
    (typeof location !== "undefined" && location.protocol) ||
    "https:";
  const host = normalizeMediaHost(cfg && cfg.mediaServer);
  return `${protocol}//${host}/${filename}`;
}

export function normalizeMediaHostForPreconnect(
  mediaServer: string | null | undefined,
): string {
  const host = normalizeMediaHost(mediaServer);
  if (host.indexOf("/proxy/") !== -1 && typeof location !== "undefined") {
    return location.host;
  }
  return host;
}
